import { useCallback, useEffect, useMemo, useState } from "react";
import { useTran } from "@/hooks";

import { useAuthStore } from "@/features/auth";
import { showToast } from "@/lib/utils/toast";
import { useRolesPermissionsStore } from "./useRolesPermissionsStore";
import { useOrganizationStore } from "@/features/organization";
import { useCompanyStore } from "@/features/company";
import { useUserStore } from "./useUserStore";
import {
  filterModulesByAudience,
  formatRoleLabel,
} from "../utils/roles-permissions";
import {
  buildRoleOptions,
  resolveModuleAudience,
} from "../utils/user-role-options";

const ADMIN_ROLES = ["system_admin", "business_unit"];
const STATUS_OPTIONS = ["active", "inactive", "deleted"];

export const useManageUserPageData = ({ userId }) => {
  const { t } = useTran();
  const { userRole, user } = useAuthStore();
  const {
    loadManagedUser,
    getEditForm,
    getEditMeta,
    isLoading,
    isSaving,
    getError,
    updateEditField,
    setEditFields,
    updateManagedUser,
    invalidateUser,
    getUser,
    setEditPermissionOverrides,
    sendingPasswordReset,
    sendPasswordReset,
  } = useUserStore();
  const {
    roles,
    permissionsModules,
    loadingModules,
    loadingRoles,
    fetchRoleList,
    fetchPermissionModules,
  } = useRolesPermissionsStore();
  const [reloadKey, setReloadKey] = useState(0);
  const [orgLoaded, setOrgLoaded] = useState(false);
  const {
    tree: organizationTree,
    loading: organizationLoading,
    fetchHierarchy,
  } = useOrganizationStore();
  const {
    companyOptions,
    companiesLoading,
    companiesLoaded,
    fetchPublicCompanies,
  } = useCompanyStore();
  const [companiesLoadedFlag, setCompaniesLoadedFlag] = useState(false);

  const isCompanyAdmin = ADMIN_ROLES.includes(userRole);
  const companyId = user?.companyId;

  useEffect(() => {
    if (!userId) return;

    let isActive = true;
    const fetchDetails = async () => {
      try {
        await loadManagedUser(userId, { force: reloadKey > 0 });
      } catch (_error) {
        if (!isActive) return;
      }
    };

    fetchDetails();

    return () => {
      isActive = false;
    };
  }, [loadManagedUser, reloadKey, userId]);

  useEffect(() => {
    fetchRoleList();
  }, [fetchRoleList]);

  useEffect(() => {
    if (!permissionsModules.length && !loadingModules) {
      fetchPermissionModules({ audience: "all" });
    }
  }, [fetchPermissionModules, loadingModules, permissionsModules.length]);

  useEffect(() => {
    if (orgLoaded) return;
    if (organizationTree.length > 0) {
      setOrgLoaded(true);
      return;
    }
    fetchHierarchy()
      .catch(() => {})
      .finally(() => setOrgLoaded(true));
  }, [fetchHierarchy, orgLoaded, organizationTree.length]);

  useEffect(() => {
    if (companiesLoadedFlag) return;
    if (companiesLoaded || companyOptions.length > 0) {
      setCompaniesLoadedFlag(true);
      return;
    }
    fetchPublicCompanies()
      .catch(() => {})
      .finally(() => setCompaniesLoadedFlag(true));
  }, [
    companiesLoaded,
    companiesLoadedFlag,
    companyOptions.length,
    fetchPublicCompanies,
  ]);

  const editForm = getEditForm(userId);
  const meta = getEditMeta(userId);
  const storedUser = userId ? getUser(userId) : null;
  const loading = Boolean(userId && isLoading(userId));
  const saving = Boolean(userId && isSaving(userId));
  const errorMessage = userId ? getError(userId) : "";

  const displayLabel =
    editForm.username ||
    editForm.email ||
    storedUser?.username ||
    storedUser?.email ||
    "";

  const roleOptions = useMemo(
    () =>
      buildRoleOptions({
        roles,
        companyId,
        currentRoleKey: userRole,
        selectedRole: editForm.role,
        t,
      }),
    [companyId, editForm.role, roles, t, userRole],
  );

  const moduleAudience = useMemo(
    () =>
      resolveModuleAudience({
        roleValue: editForm.role,
        roles,
        isCompanyAdmin,
      }),
    [editForm.role, isCompanyAdmin, roles],
  );

  const permissionModules = useMemo(
    () => filterModulesByAudience(permissionsModules, moduleAudience),
    [moduleAudience, permissionsModules],
  );

  const companyLoading = companiesLoading;

  const formatStatusLabel = useCallback(
    (value) => {
      if (!value) return "";
      if (value === "active") return t("Active");
      if (value === "inactive") return t("Disabled");
      if (value === "deleted") return t("Deleted");
      return formatRoleLabel(value);
    },
    [t],
  );

  const statusOptions = useMemo(
    () =>
      STATUS_OPTIONS.map((value) => ({
        value,
        label: formatStatusLabel(value),
      })),
    [formatStatusLabel],
  );

  const statusValue = editForm.status || storedUser?.status || "";
  const statusLabel = statusValue
    ? formatStatusLabel(statusValue)
    : t("Not set");
  const statusVariant = ["active", "verified"].includes(statusValue)
    ? "default"
    : "outline";

  const formatDateTime = useCallback(
    (value) => {
      if (!value) return t("Not available");
      const date = typeof value === "string" ? new Date(value) : value;
      if (Number.isNaN(date.getTime())) return t("Not available");
      return date.toLocaleString();
    },
    [t],
  );

  const handleRetry = () => {
    if (!userId) return;
    invalidateUser(userId);
    setReloadKey((prev) => prev + 1);
  };

  const handleChange = (field) => (event) => {
    if (!userId) return;
    updateEditField(userId, field, event.target.value);
  };

  const handleSelectChange = (field) => (input) => {
    if (!userId) return;
    const value = input?.target ? input.target.value : input;
    updateEditField(userId, field, value);
  };

  const handleOrganizationChange = (value, label) => {
    if (!userId) return;
    setEditFields(userId, {
      organizationUnitId: value || "",
      organizationUnitName: label || editForm.organizationUnitName || "",
    });
  };

  const handleCompanyChange = (input) => {
    if (!userId) return;
    const value = input?.target ? input.target.value : input;
    setEditFields(userId, {
      companyId: value || "",
      organizationUnitId: "",
      organizationUnitName: "",
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!userId || saving) return;

    const trimmedCompanyId = editForm.companyId?.trim();
    const trimmedOrganizationId = editForm.organizationUnitId?.trim();

    const payload = {
      name: editForm.name,
      username: editForm.username,
      email: editForm.email,
      role: editForm.role,
      status: editForm.status,
      organizationUnitId: isCompanyAdmin
        ? null
        : trimmedOrganizationId || undefined,
      companyId: isCompanyAdmin ? trimmedCompanyId || undefined : undefined,
      permissionOverrides: editForm.permissionOverrides,
    };

    try {
      await updateManagedUser(userId, payload);
    } catch (_error) {
      return;
    }
  };

  const handlePermissionOverridesChange = (nextOverrides) => {
    if (!userId) return;
    setEditPermissionOverrides(userId, nextOverrides);
  };

  const handlePasswordReset = async () => {
    const email = editForm.email || storedUser?.email || "";
    await sendPasswordReset(email, { showToast: true, t });
  };

  return {
    editForm,
    meta,
    loading,
    saving,
    errorMessage,
    displayLabel,
    roleOptions,
    loadingRoles,
    statusOptions,
    statusValue,
    statusLabel,
    statusVariant,
    permissionModules,
    companyOptions,
    companyLoading,
    organizationTree,
    organizationLoading,
    isCompanyAdmin,
    sendingReset: sendingPasswordReset,
    formatDateTime,
    handleRetry,
    handleChange,
    handleSelectChange,
    handleCompanyChange,
    handleOrganizationChange,
    handleSubmit,
    handlePasswordReset,
    handlePermissionOverridesChange,
  };
};
